import React, { useState, useEffect } from 'react';
import { obtenerUrlProtegida, abrirArchivoProtegido } from '../utils/archivos';
import BarraFiltros, { SinResultados } from './BarraFiltros';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    AppBar,
    Toolbar,
    TextField,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Box,
    MenuItem,
    Select,
    InputLabel,
    FormControl,
    styled
} from '@mui/material';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus,
    faEdit,
    faTrash,
    faEye,
    faFile,
    faFileUpload,
    faFilePdf
} from '@fortawesome/free-solid-svg-icons';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.hmsaa.com.mx:5001';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    fontWeight: 'bold',
}));

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

function FileUploadField({
    label,
    value,
    onFileChange,
    disabled,
    acceptedFormats = "application/pdf",
    onPreview
}) {
    const fileName = value?.name || (typeof value === 'string' ? value.split('/').pop().replace(/^[0-9a-f]{12}_/, '') : '');

    return (
        <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
                {label}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button
                    component="label"
                    variant="outlined"
                    color="primary"
                    startIcon={<FontAwesomeIcon icon={faFileUpload} />}
                    disabled={disabled}
                >
                    Seleccionar archivo
                    <VisuallyHiddenInput
                        type="file"
                        accept={acceptedFormats}
                        onChange={onFileChange}
                        disabled={disabled}
                    />
                </Button>

                {fileName && (
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FontAwesomeIcon icon={faFilePdf} color="#FF0000" />
                        {fileName}
                    </Typography>
                )}

                {!fileName && (
                    <Typography variant="body2" color="textSecondary">
                        Ningún archivo seleccionado
                    </Typography>
                )}
            </Box>

            {value && (
                <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<FontAwesomeIcon icon={faEye} />}
                        onClick={() => onPreview(value)}
                        disabled={!value}
                    >
                        Previsualizar
                    </Button>
                </Box>
            )}
        </Box>
    );
}

function ProveedoresPage() {
    const [proveedores, setProveedores] = useState([]);
    const [search, setSearch] = useState('');
    const [tipoFiltro, setTipoFiltro] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [archivosModalOpen, setArchivosModalOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [currentProveedor, setCurrentProveedor] = useState(null);
    const [newProveedor, setNewProveedor] = useState({
        razon_social: '',
        rfc: '',
        curp: '',
        tipo_persona: '',
        constancia_fiscal: null,
        opinion_cumplimiento: null,
        ine: null,
        comprobante_domicilio: null,
        estado_cuenta_bancario: null,
        referencia_comercial_1: '',
        referencia_comercial_2: '',
        referencia_comercial_3: '',
        acta_constitutiva: null,
        poder_representante: null,
        ine_representante: null,
    });
    const [proveedorArchivos, setProveedorArchivos] = useState(null);

    useEffect(() => {
        fetchProveedores();
    }, []);

    const fetchProveedores = async () => {
        try {
            const response = await fetch(`${API_URL}/api/proveedores`);
            if (!response.ok) throw new Error('Error al cargar los proveedores');
            const data = await response.json();
            setProveedores(data);
        } catch (error) {
            Swal.fire('Error', error.message, 'error');
        }
    };

    // En la función handlePreview
    const handlePreview = async (url) => {
        if (!url) {
            Swal.fire('Error', 'No hay archivo disponible para previsualizar.', 'error');
            return;
        }

        let fileUrl;
        try {
            fileUrl = await obtenerUrlProtegida(`${API_URL}/uploads/${url}`);
        } catch (error) {
            Swal.fire('Error', error.message, 'error');
            return;
        }

        // Cerrar el modal de archivos antes de abrir el modal de previsualización
        setArchivosModalOpen(false);

        Swal.fire({
            title: 'Vista previa del documento',
            html: `
            <div style="position: relative; height: 70vh;">
                <iframe 
                    src="${fileUrl}" 
                    style="width: 100%; height: 100%; border: none;"
                ></iframe>
            </div>
        `,
            showCancelButton: true,
            confirmButtonText: 'Descargar',
            cancelButtonText: 'Cerrar',
            width: '80%',
            customClass: {
                popup: 'preview-modal',
                content: 'preview-content'
            },
            didOpen: () => {
                Swal.getConfirmButton().addEventListener('click', () => {
                    window.open(fileUrl, '_blank');
                });
            },
            didClose: () => {
                // Reabrir el modal de archivos después de cerrar el modal de previsualización
                setArchivosModalOpen(true);
                setTimeout(() => URL.revokeObjectURL(fileUrl), 60000);
            }
        });
    };

    const handleDownload = async (url) => {
        if (!url) {
            Swal.fire('Error', 'No hay archivo disponible para descargar.', 'error');
            return;
        }

        try {
            await abrirArchivoProtegido(`${API_URL}/uploads/${url}`);
        } catch (error) {
            Swal.fire('Error', error.message, 'error');
        }
    };

    const handleFileChange = (e, field) => {
        const file = e.target.files[0];
        if (file && file.type === 'application/pdf') {
            setNewProveedor({ ...newProveedor, [field]: file });
        } else {
            Swal.fire('Error', 'Solo se permiten archivos PDF.', 'error');
        }
    };

    const handleSaveProveedor = async () => {
        const formData = new FormData();
        Object.entries(newProveedor).forEach(([key, value]) => {
            if (value) formData.append(key, value);
        });

        const url = editMode
            ? `${API_URL}/api/proveedores/${currentProveedor.id}`
            : `${API_URL}/api/proveedores`;
        const method = editMode ? 'PUT' : 'POST';

        try {
            const response = await fetch(url, { method, body: formData });
            if (!response.ok) throw new Error('Error al guardar el proveedor');
            const savedProveedor = await response.json();

            if (editMode) {
                setProveedores((prev) =>
                    prev.map((p) => (p.id === savedProveedor.id ? savedProveedor : p))
                );
            } else {
                setProveedores((prev) => [...prev, savedProveedor]);
            }

            Swal.fire('Éxito', `Proveedor ${editMode ? 'actualizado' : 'creado'} correctamente.`, 'success');
            setDialogOpen(false);
        } catch (error) {
            Swal.fire('Error', error.message, 'error');
        }
    };

    const handleDeleteProveedor = async (id) => {
        Swal.fire({
            title: '¿Estás seguro?',
            text: 'Esta acción no se puede deshacer.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const response = await fetch(`${API_URL}/api/proveedores/${id}`, {
                        method: 'DELETE',
                    });
                    if (!response.ok) throw new Error('Error al eliminar el proveedor');
                    setProveedores((prev) => prev.filter((p) => p.id !== id));
                    Swal.fire('Eliminado', 'El proveedor ha sido eliminado.', 'success');
                } catch (error) {
                    Swal.fire('Error', error.message, 'error');
                }
            }
        });
    };

    const handleOpenArchivosModal = (proveedor) => {
        setProveedorArchivos(proveedor);
        setArchivosModalOpen(true);
    };

    const termino = search.trim().toLowerCase();

    const filteredProveedores = proveedores.filter((proveedor) =>
        (!tipoFiltro || proveedor.tipo_persona === tipoFiltro) &&
        [proveedor.razon_social, proveedor.rfc, proveedor.curp].some((v) =>
            (v ?? '').toString().toLowerCase().includes(termino)
        )
    );

    const limpiarFiltros = () => {
        setSearch('');
        setTipoFiltro('');
    };

    const fileFields = [
        { field: 'constancia_fiscal', label: 'Constancia Fiscal' },
        { field: 'opinion_cumplimiento', label: 'Opinión de Cumplimiento' },
        { field: 'ine', label: 'INE', disabled: newProveedor.tipo_persona !== 'Física' },
        { field: 'comprobante_domicilio', label: 'Comprobante de Domicilio' },
        { field: 'estado_cuenta_bancario', label: 'Estado de Cuenta' },
        { field: 'acta_constitutiva', label: 'Acta Constitutiva', disabled: newProveedor.tipo_persona === 'Física' },
        { field: 'poder_representante', label: 'Poder Representante', disabled: newProveedor.tipo_persona === 'Física' },
        { field: 'ine_representante', label: 'INE Representante', disabled: newProveedor.tipo_persona === 'Física' },
    ];

    return (
        <div>
            <AppBar position="static">
                <Toolbar>
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                        Gestión de Proveedores
                    </Typography>
                    <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<FontAwesomeIcon icon={faPlus} />}
                        onClick={() => {
                            setNewProveedor({
                                razon_social: '',
                                rfc: '',
                                curp: '',
                                tipo_persona: '',
                                constancia_fiscal: null,
                                opinion_cumplimiento: null,
                                ine: null,
                                comprobante_domicilio: null,
                                estado_cuenta_bancario: null,
                                referencia_comercial_1: '',
                                referencia_comercial_2: '',
                                referencia_comercial_3: '',
                                acta_constitutiva: null,
                                poder_representante: null,
                                ine_representante: null,
                            });
                            setEditMode(false);
                            setDialogOpen(true);
                        }}
                    >
                        Agregar Proveedor
                    </Button>
                </Toolbar>
            </AppBar>

            <BarraFiltros
                busqueda={search}
                onBusqueda={setSearch}
                placeholder="Buscar por razón social, RFC o CURP"
                filtros={[
                    {
                        id: 'tipo',
                        tipo: 'chips',
                        etiqueta: 'Tipo de persona',
                        valor: tipoFiltro,
                        onChange: setTipoFiltro,
                        opciones: [
                            { valor: '', etiqueta: 'Todos' },
                            { valor: 'Física', etiqueta: 'Física' },
                            { valor: 'Moral', etiqueta: 'Moral' },
                        ],
                    },
                ]}
                total={proveedores.length}
                mostrados={filteredProveedores.length}
                onLimpiar={limpiarFiltros}
            />

            <TableContainer component={Paper} sx={{ margin: '20px auto', maxWidth: 1200 }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <StyledTableCell>Razón Social</StyledTableCell>
                            <StyledTableCell>RFC</StyledTableCell>
                            <StyledTableCell>CURP</StyledTableCell>
                            <StyledTableCell>Tipo Persona</StyledTableCell>
                            <StyledTableCell>Archivos</StyledTableCell>
                            <StyledTableCell>Acciones</StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredProveedores.map((proveedor) => (
                            <TableRow key={proveedor.id}>
                                <TableCell>{proveedor.razon_social}</TableCell>
                                <TableCell>{proveedor.rfc}</TableCell>
                                <TableCell>{proveedor.curp || 'N/A'}</TableCell>
                                <TableCell>{proveedor.tipo_persona}</TableCell>
                                <TableCell>
                                    <Button
                                        variant="contained"
                                        color="info"
                                        startIcon={<FontAwesomeIcon icon={faFile} />}
                                        onClick={() => handleOpenArchivosModal(proveedor)}
                                    >
                                        Ver Archivos
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Box display="flex" gap={1}>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            startIcon={<FontAwesomeIcon icon={faEdit} />}
                                            onClick={() => {
                                                setCurrentProveedor(proveedor);
                                                setNewProveedor(proveedor);
                                                setEditMode(true);
                                                setDialogOpen(true);
                                            }}
                                        >
                                            Editar
                                        </Button>
                                        <Button
                                            variant="contained"
                                            color="error"
                                            startIcon={<FontAwesomeIcon icon={faTrash} />}
                                            onClick={() => handleDeleteProveedor(proveedor.id)}
                                        >
                                            Eliminar
                                        </Button>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                        {filteredProveedores.length === 0 && (
                            <SinResultados colSpan={6} total={proveedores.length} onLimpiar={limpiarFiltros} entidad="proveedores" />
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="md">
                <DialogTitle>
                    {editMode ? 'Editar Proveedor' : 'Agregar Proveedor'}
                </DialogTitle>
                <DialogContent dividers>
                    <TextField
                        margin="dense"
                        label="Razón Social"
                        fullWidth
                        value={newProveedor.razon_social}
                        onChange={(e) =>
                            setNewProveedor({ ...newProveedor, razon_social: e.target.value })
                        }
                    />
                    <TextField
                        margin="dense"
                        label="RFC"
                        fullWidth
                        value={newProveedor.rfc}
                        onChange={(e) => setNewProveedor({ ...newProveedor, rfc: e.target.value })}
                    />
                    <FormControl fullWidth margin="dense">
                        <InputLabel>Tipo de Persona</InputLabel>
                        <Select
                            value={newProveedor.tipo_persona}
                            onChange={(e) =>
                                setNewProveedor({ ...newProveedor, tipo_persona: e.target.value })
                            }
                        >
                            <MenuItem value="">Seleccionar</MenuItem>
                            <MenuItem value="Física">Física</MenuItem>
                            <MenuItem value="Moral">Moral</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField
                        margin="dense"
                        label="CURP"
                        fullWidth
                        disabled={newProveedor.tipo_persona !== 'Física'}
                        value={newProveedor.curp || ''}
                        onChange={(e) =>
                            setNewProveedor({ ...newProveedor, curp: e.target.value })
                        }
                    />
                    {fileFields.map(({ field, label, disabled }) => (
                        <FileUploadField
                            key={field}
                            label={label}
                            value={newProveedor[field]}
                            onFileChange={(e) => handleFileChange(e, field)}
                            disabled={disabled}
                            onPreview={handlePreview}
                        />
                    ))}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)} color="secondary">
                        Cancelar
                    </Button>
                    <Button onClick={handleSaveProveedor} color="primary">
                        Guardar
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={archivosModalOpen}
                onClose={() => setArchivosModalOpen(false)}
                fullWidth
                maxWidth="md"
                sx={{
                    '& .MuiDialog-paper': {
                        maxWidth: '90%',
                        maxHeight: '90vh',
                    },
                    '& .MuiDialogContent-root': {
                        padding: 3,
                    }
                }}
            >
                <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 'bold', borderBottom: '1px solid #e0e0e0' }}>
                    Archivos del Proveedor
                </DialogTitle>
                <DialogContent dividers>
                    {fileFields.map(({ field, label }) => {
                        const archivoSubido = proveedorArchivos?.[field];
                        return (
                            <Box
                                key={field}
                                sx={{
                                    mb: 2,
                                    p: 2,
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '8px',
                                    backgroundColor: '#f9f9f9',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between'
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <FontAwesomeIcon
                                        icon={archivoSubido ? faFilePdf : faFile}
                                        color={archivoSubido ? '#FF0000' : '#757575'}
                                        fontSize="1.5rem"
                                    />
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                        {label}
                                    </Typography>
                                </Box>
                                {archivoSubido ? (
                                    <Button
                                        variant="contained"
                                        startIcon={<FontAwesomeIcon icon={faEye} />}
                                        onClick={() => handlePreview(archivoSubido)}
                                        sx={{ textTransform: 'none' }}
                                    >
                                        Previsualizar PDF
                                    </Button>
                                ) : (
                                    <Typography variant="body2" color="textSecondary">
                                        No subido
                                    </Typography>
                                )}
                            </Box>
                        );
                    })}
                </DialogContent>
                <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
                    <Button
                        onClick={() => setArchivosModalOpen(false)}
                        color="secondary"
                        sx={{ textTransform: 'none' }}
                    >
                        Cerrar
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default ProveedoresPage;